/**
 * Meal Plan Integration Service
 * 
 * Handles pushing nutrition planner week plans to the user's meal plan calendar.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * Get auth headers for API requests
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

/**
 * Get the current user's ID from the token
 */
const getUserId = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    // JWT tokens have 3 parts: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Token is not a valid JWT format');
      return null;
    }
    
    // Decode base64url (not standard base64)
    // Replace - with + and _ with /
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (payload.length % 4) {
      payload += '=';
    }
    
    const decoded = JSON.parse(atob(payload));
    return decoded.sub || decoded.userId || decoded.id;
  } catch (e) {
    console.error('Failed to parse user ID from token:', e);
    return null;
  }
};

/**
 * Convert week plan to meal plan format
 * 
 * The meal plan API expects:
 * - menuItemId: ObjectId (for menu items only)
 * - date: Date
 * - mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack'
 * 
 * Note: Individual foods (from USDA) cannot be directly added to meal plan
 * as the current MealPlan model only supports MenuItem references.
 * 
 * @param {Array} weekPlan - The week plan from useNutritionCart
 * @param {Date} startDate - Starting date for the week
 * @returns {Object} { menuItems: Array, foods: Array } - Separated items
 */
export const convertWeekPlanToMealPlanFormat = (weekPlan, startDate = new Date()) => {
  const menuItems = [];
  const foods = [];
  
  weekPlan.forEach(day => {
    const dayDate = new Date(day.date);
    
    ['breakfast', 'lunch', 'dinner', 'snacks'].forEach(mealType => {
      const meal = day.meals[mealType];
      
      // Menu items can be added to meal plan
      meal.menuItems.forEach(item => {
        // Add one entry per serving
        for (let i = 0; i < item.servings; i++) {
          menuItems.push({
            menuItemId: item.menuItem._id,
            date: dayDate.toISOString(),
            // Convert 'snacks' to 'snack' for meal plan API
            mealType: mealType === 'snacks' ? 'snack' : mealType,
            // Additional info for reference
            title: item.menuItem.title,
            dayNumber: day.dayNumber
          });
        }
      });
      
      // Individual foods (USDA) - include nutrients for display
      meal.foods.forEach(item => {
        foods.push({
          fdcId: item.food.fdcId,
          description: item.food.description,
          category: item.food.category,
          nutrients: item.food.nutrients,
          quantity: item.quantity,
          servingSize: item.servingSize,
          brandOwner: item.food.brandOwner,
          date: dayDate.toISOString(),
          mealType: mealType === 'snacks' ? 'snack' : mealType,
          dayNumber: day.dayNumber
        });
      });
    });
  });
  
  return { menuItems, foods };
};

/**
 * Sync nutrition planner week plan to the user's meal plan calendar.
 * 
 * Unlike pushWeekPlanToCalendar (which blindly adds), this function:
 * 1. Fetches existing calendar entries for the week's date range
 * 2. Removes calendar entries that are no longer in the nutrition plan
 * 3. Only adds items that don't already exist on the calendar
 *
 * @param {Array} weekPlan - The week plan from useNutritionCart
 * @returns {Promise<Object>} Result with counts of added/removed items
 */
export const syncWeekPlanToCalendar = async (weekPlan) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not logged in. Please log in to sync your meal plan.');
  }

  const userId = getUserIdWithFallback();
  if (!userId) {
    throw new Error('Unable to determine user ID. Please try logging out and back in.');
  }

  const results = { added: 0, removed: 0, unchanged: 0, errors: [] };

  if (!weekPlan || weekPlan.length === 0) return results;

  // 1. Build desired state from the nutrition week plan
  const { menuItems: desiredMenuItems, foods: desiredFoods } =
    convertWeekPlanToMealPlanFormat(weekPlan);

  // 2. Determine date range covered by the week plan
  const dates = weekPlan.map(d => new Date(d.date));
  const startDate = new Date(Math.min(...dates));
  const endDate = new Date(Math.max(...dates));
  // Extend endDate to end-of-day so same-day comparisons work
  endDate.setHours(23, 59, 59, 999);

  // 3. Fetch existing calendar entries for that range
  let existingMeals = [];
  try {
    existingMeals = await getMealPlanForDateRange(startDate, endDate);
  } catch {
    // If fetch fails, fall through to add-only mode
  }

  // 4. Build lookup keys for existing meals
  //    Menu item key: menuItemId + dateStr + mealType
  //    Food key: fdcId + dateStr + mealType
  const normalizeDate = (d) => new Date(d).toISOString().slice(0, 10);

  const existingMenuKeys = new Map(); // key -> mealId
  const existingFoodKeys = new Map();

  existingMeals.forEach(meal => {
    const dateStr = normalizeDate(meal.date);
    if (meal.isFood && meal.food) {
      const key = `food:${meal.food.fdcId}:${dateStr}:${meal.mealType}`;
      // Store first occurrence; if duplicates exist they'll be cleaned
      if (!existingFoodKeys.has(key)) {
        existingFoodKeys.set(key, meal._id);
      }
    } else if (meal.menuItemId) {
      const menuId = typeof meal.menuItemId === 'object'
        ? (meal.menuItemId._id || meal.menuItemId)
        : meal.menuItemId;
      const key = `menu:${menuId}:${dateStr}:${meal.mealType}`;
      if (!existingMenuKeys.has(key)) {
        existingMenuKeys.set(key, meal._id);
      }
    }
  });

  // 5. Build desired keys
  const desiredMenuKeys = new Set();
  desiredMenuItems.forEach(item => {
    const dateStr = normalizeDate(item.date);
    const key = `menu:${item.menuItemId}:${dateStr}:${item.mealType}`;
    desiredMenuKeys.add(key);
  });

  const desiredFoodKeys = new Set();
  desiredFoods.forEach(food => {
    const dateStr = normalizeDate(food.date);
    const key = `food:${food.fdcId}:${dateStr}:${food.mealType}`;
    desiredFoodKeys.add(key);
  });

  // 6. Remove existing entries that are no longer in the desired plan
  const toRemove = [];
  existingMenuKeys.forEach((mealId, key) => {
    if (!desiredMenuKeys.has(key)) toRemove.push(mealId);
  });
  existingFoodKeys.forEach((mealId, key) => {
    if (!desiredFoodKeys.has(key)) toRemove.push(mealId);
  });

  for (const mealId of toRemove) {
    try {
      const resp = await fetch(`${API_URL}/mealplan/${userId}/meals/${mealId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (resp.ok) results.removed++;
    } catch (err) {
      results.errors.push({ action: 'remove', id: mealId, error: err.message });
    }
  }

  // 7. Add items that don't yet exist on the calendar
  for (const item of desiredMenuItems) {
    const dateStr = normalizeDate(item.date);
    const key = `menu:${item.menuItemId}:${dateStr}:${item.mealType}`;
    if (existingMenuKeys.has(key)) {
      results.unchanged++;
      continue;
    }
    try {
      const resp = await fetch(`${API_URL}/mealplan/${userId}/meals`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          menuItemId: item.menuItemId,
          date: item.date,
          mealType: item.mealType
        })
      });
      if (resp.ok) results.added++;
      else results.errors.push({ action: 'add', item: item.title, error: 'Request failed' });
    } catch (err) {
      results.errors.push({ action: 'add', item: item.title, error: err.message });
    }
  }

  for (const food of desiredFoods) {
    const dateStr = normalizeDate(food.date);
    const key = `food:${food.fdcId}:${dateStr}:${food.mealType}`;
    if (existingFoodKeys.has(key)) {
      results.unchanged++;
      continue;
    }
    try {
      const resp = await fetch(`${API_URL}/mealplan/${userId}/meals`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          date: food.date,
          mealType: food.mealType,
          isFood: true,
          food: {
            fdcId: food.fdcId,
            description: food.description,
            category: food.category || 'Other',
            nutrients: food.nutrients || {},
            quantity: food.quantity || 1,
            servingSize: food.servingSize || 100,
            brandOwner: food.brandOwner
          }
        })
      });
      if (resp.ok) results.added++;
      else results.errors.push({ action: 'add', item: food.description, error: 'Request failed' });
    } catch (err) {
      results.errors.push({ action: 'add', item: food.description, error: err.message });
    }
  }

  return results;
};

/**
 * Push nutrition planner week to the user's meal plan calendar
 * 
 * @param {Array} weekPlan - The week plan from useNutritionCart
 * @param {Date} startDate - Starting date for the week
 * @returns {Promise<Object>} Result with success count and any errors
 */
export const pushWeekPlanToCalendar = async (weekPlan, startDate = new Date()) => {
  // First check if we have a token at all
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not logged in. Please log in to add items to your meal plan.');
  }
  
  let userId = getUserId();
  
  // If we couldn't parse the userId from token, try to get it from user info
  if (!userId) {
    // Try getting user from localStorage (some auth flows store it separately)
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        userId = user.sub || user.id || user.userId;
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
      }
    }
  }
  
  if (!userId) {
    throw new Error('Unable to determine user ID. Please try logging out and back in.');
  }
  
  const { menuItems, foods } = convertWeekPlanToMealPlanFormat(weekPlan, startDate);
  
  const results = {
    success: 0,
    failed: 0,
    errors: [],
    menuItemsAdded: 0,
    foodsAdded: 0
  };
  
  // Add menu items one by one
  for (const item of menuItems) {
    try {
      const response = await fetch(`${API_URL}/mealplan/${userId}/meals`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          menuItemId: item.menuItemId,
          date: item.date,
          mealType: item.mealType
        })
      });
      
      if (response.ok) {
        results.success++;
        results.menuItemsAdded++;
      } else {
        const error = await response.json();
        results.failed++;
        results.errors.push({
          item: item.title,
          error: error.error || 'Failed to add'
        });
      }
    } catch (error) {
      results.failed++;
      results.errors.push({
        item: item.title,
        error: error.message
      });
    }
  }
  
  // Add individual foods
  for (const food of foods) {
    try {
      const response = await fetch(`${API_URL}/mealplan/${userId}/meals`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          date: food.date,
          mealType: food.mealType,
          isFood: true,
          food: {
            fdcId: food.fdcId,
            description: food.description,
            category: food.category || 'Other',
            nutrients: food.nutrients || {},
            quantity: food.quantity || 1,
            servingSize: food.servingSize || 100,
            brandOwner: food.brandOwner
          }
        })
      });
      
      if (response.ok) {
        results.success++;
        results.foodsAdded++;
      } else {
        const error = await response.json();
        results.failed++;
        results.errors.push({
          item: food.description,
          error: error.error || 'Failed to add food'
        });
      }
    } catch (error) {
      results.failed++;
      results.errors.push({
        item: food.description,
        error: error.message
      });
    }
  }
  
  return results;
};

/**
 * Get user ID with fallback options
 */
const getUserIdWithFallback = () => {
  let userId = getUserId();
  
  if (!userId) {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        userId = user.sub || user.id || user.userId;
      } catch (e) {
        console.error('Failed to parse user from localStorage:', e);
      }
    }
  }
  
  return userId;
};

/**
 * Get meal plan entries for a date range
 * 
 * @param {Date} startDate 
 * @param {Date} endDate 
 * @returns {Promise<Array>}
 */
export const getMealPlanForDateRange = async (startDate, endDate) => {
  const userId = getUserIdWithFallback();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  
  const response = await fetch(`${API_URL}/mealplan/${userId}/meals`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch meal plan');
  }
  
  const mealPlan = await response.json();
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Filter meals within date range
  return (mealPlan.meals || []).filter(meal => {
    const mealDate = new Date(meal.date);
    return mealDate >= start && mealDate <= end;
  });
};

/**
 * Check if there are conflicts with existing meal plan entries
 * 
 * @param {Array} weekPlan - The week plan to check
 * @returns {Promise<Object>} Conflict information
 */
export const checkForConflicts = async (weekPlan) => {
  if (!weekPlan || weekPlan.length === 0) {
    return { hasConflicts: false, conflicts: [] };
  }
  
  // Check if user is authenticated before making API call
  const userId = getUserIdWithFallback();
  if (!userId) {
    // If not authenticated, skip conflict check (will be caught later)
    return { hasConflicts: false, conflicts: [] };
  }
  
  const startDate = new Date(weekPlan[0].date);
  const endDate = new Date(weekPlan[weekPlan.length - 1].date);
  
  try {
    const existingMeals = await getMealPlanForDateRange(startDate, endDate);
    
    if (existingMeals.length === 0) {
      return { hasConflicts: false, conflicts: [] };
    }
    
    const conflicts = [];
    
    weekPlan.forEach(day => {
      const dayDate = new Date(day.date).toDateString();
      const dayMeals = existingMeals.filter(m => 
        new Date(m.date).toDateString() === dayDate
      );
      
      if (dayMeals.length > 0) {
        conflicts.push({
          date: day.date,
          dayName: day.dayName,
          existingCount: dayMeals.length
        });
      }
    });
    
    return {
      hasConflicts: conflicts.length > 0,
      conflicts
    };
  } catch (error) {
    console.error('Error checking conflicts:', error);
    // Don't fail the whole operation if conflict check fails
    return { hasConflicts: false, conflicts: [], error: error.message };
  }
};

export default {
  pushWeekPlanToCalendar,
  syncWeekPlanToCalendar,
  convertWeekPlanToMealPlanFormat,
  getMealPlanForDateRange,
  checkForConflicts
};
